import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { Role, ClaimType, UserClaim } from '../../types/user-management';
import { UserManagementService } from '../../services/userManagementService';

interface RoleManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  roles?: Role[];
  availableClaims: ClaimType[];
  onRefresh: () => void;
}

const RoleManagementModal: React.FC<RoleManagementModalProps> = ({
  isOpen,
  onClose,
  roles: initialRoles = [],
  availableClaims,
  onRefresh
}) => {
  const [activeTab, setActiveTab] = useState<'create' | 'manage'>('create');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleAbbreviation, setNewRoleAbbreviation] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>(''); // role name
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [selectedClaimType, setSelectedClaimType] = useState('');
  const [claimValue, setClaimValue] = useState('True');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>(initialRoles);

  // Keep internal roles list in sync with incoming prop
  useEffect(() => {
    setRoles(initialRoles || []);
  }, [initialRoles]);

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRoleName || !newRoleAbbreviation) return;

    setIsLoading(true);
    setError(null);

    try {
      await UserManagementService.createRole({
        name: newRoleName,
        abbreviation: newRoleAbbreviation
      });
      
      // Add to local state
      const newRole: Role = {
        name: newRoleName,
        abbreviation: newRoleAbbreviation,
        claims: []
      };
      setRoles([...roles, newRole]);
      
      setNewRoleName('');
      setNewRoleAbbreviation('');
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create role');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRole = async (roleName: string) => {
    if (!confirm(`Are you sure you want to delete the role "${roleName}"?`)) return;

    setIsLoading(true);
    setError(null);

    try {
      await UserManagementService.deleteRole(roleName);
      
      // Remove from local state
      setRoles(roles.filter(r => r.name !== roleName));
      
      // Reset selected role if it was deleted
      if (selectedRole === roleName) {
        setSelectedRole('');
      }
      
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete role');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClaimToRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole || !selectedClaimType) return;

    setIsLoading(true);
    setError(null);

    try {
  const newClaim = { type: selectedClaimType, value: claimValue };
  const roleObj = roles.find(r => r.name === selectedRole);
  await UserManagementService.addClaimToRole({ id: (roleObj as any)?.id, name: selectedRole }, newClaim);
      
      // Update local state
      setRoles(roles.map(role => {
        if (role.name === selectedRole) {
          return {
            ...role,
            claims: [...(role.claims || []), newClaim]
          };
        }
        return role;
      }));
      
      setSelectedClaimType('');
      setClaimValue('True');
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add claim to role');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveClaimFromRole = async (roleName: string, claim: UserClaim) => {
    setIsLoading(true);
    setError(null);

    try {
      const roleObj = roles.find(r => r.name === roleName);
      await UserManagementService.removeClaimFromRole({ id: (roleObj as any)?.id, name: roleName }, claim);
      
      // Update local state
      setRoles(roles.map(role => {
        if (role.name === roleName) {
          return {
            ...role,
            claims: (role.claims || []).filter(c => 
              !(c.type === claim.type && c.value === claim.value)
            )
          };
        }
        return role;
      }));
      
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove claim from role');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedRoleData = roles.find(r => r.name === selectedRole);

  // When selectedRole changes, fetch its claims (source of truth) and merge
  useEffect(() => {
    const loadRoleClaims = async () => {
      if (!isOpen || !selectedRole) return;
      const roleObj: any = roles.find(r => r.name === selectedRole);
      const roleId = roleObj?.id || selectedRoleId;
      if (!roleId) return;
      try {
        const claims = await UserManagementService.getRoleClaims(roleId);
        setRoles(prev => prev.map(r => r.name === selectedRole ? { ...r, claims } : r));
      } catch (e) {
        console.error('Failed to load role claims', e);
      }
    };
    loadRoleClaims();
  }, [selectedRole, isOpen, selectedRoleId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Role Management</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            className={`py-2 px-4 -mb-px text-sm font-medium ${
              activeTab === 'create'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('create')}
          >
            Create Role
          </button>
          <button
            className={`py-2 px-4 -mb-px text-sm font-medium ${
              activeTab === 'manage'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('manage')}
          >
            Manage Roles
          </button>
        </div>

        {activeTab === 'create' && (
          <div className="space-y-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-medium mb-4">Create New Role</h3>
              <form onSubmit={handleCreateRole} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role Name
                    </label>
                    <input
                      type="text"
                      value={newRoleName}
                      onChange={(e) => setNewRoleName(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="e.g., Teacher Assistant"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Abbreviation
                    </label>
                    <input
                      type="text"
                      value={newRoleAbbreviation}
                      onChange={(e) => setNewRoleAbbreviation(e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="e.g., TA"
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isLoading || !newRoleName || !newRoleAbbreviation}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Create Role
                </button>
              </form>
            </div>

            {/* Existing roles list */}
            <div>
              <h3 className="text-lg font-medium mb-4">Existing Roles</h3>
              {roles.length === 0 ? (
                <p className="text-gray-500 italic">No roles created yet</p>
              ) : (
                <div className="space-y-2">
                  {roles.map((role) => (
                    <div
                      key={role.name}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div>
                        <span className="font-medium">{role.name}</span>
                        {role.abbreviation && (
                          <span className="text-gray-500 ml-2">({role.abbreviation})</span>
                        )}
                        {role.claims && role.claims.length > 0 && (
                          <span className="text-sm text-blue-600 ml-2">
                            {role.claims.length} claim{role.claims.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteRole(role.name)}
                        disabled={isLoading}
                        className="text-red-600 hover:text-red-700 disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'manage' && (
          <div className="space-y-6">
            {/* Role selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Role to Manage
              </label>
              <select
                value={selectedRole}
                onChange={(e) => {
                  const name = e.target.value;
                  setSelectedRole(name);
                  const roleObj: any = roles.find(r => r.name === name);
                  if (roleObj?.id) setSelectedRoleId(roleObj.id);
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
              >
                <option value="">Select a role</option>
                {roles.map((role: any) => (
                  <option key={role.id || role.name} value={role.name}>
                    {role.name}{role.abbreviation ? ` (${role.abbreviation})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {selectedRole && selectedRoleData && (
              <>
                {/* Add claim to role */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h3 className="text-lg font-medium mb-4">Add Claim to {selectedRole}</h3>
                  <form onSubmit={handleAddClaimToRole} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Claim Type
                        </label>
                        <select
                          value={selectedClaimType}
                          onChange={(e) => setSelectedClaimType(e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2"
                          required
                        >
                          <option value="">Select a claim type</option>
                          {availableClaims.map((claim) => (
                            <option key={claim.name} value={claim.name}>
                              {claim.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Value
                        </label>
                        <input
                          type="text"
                          value={claimValue}
                          onChange={(e) => setClaimValue(e.target.value)}
                          className="w-full rounded-md border border-gray-300 px-3 py-2"
                          placeholder="True"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading || !selectedClaimType}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                      Add Claim to Role
                    </button>
                  </form>
                </div>

                {/* Current role claims */}
                <div>
                  <h3 className="text-lg font-medium mb-4">Claims for {selectedRole}</h3>
                  {!selectedRoleData.claims || selectedRoleData.claims.length === 0 ? (
                    <p className="text-gray-500 italic">No claims assigned to this role</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedRoleData.claims.map((claim, index) => (
                        <div
                          key={`${claim.type}-${claim.value}-${index}`}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <span className="font-medium">{claim.type}</span>
                            <span className="text-gray-500 ml-2">= {claim.value}</span>
                          </div>
                          <button
                            onClick={() => handleRemoveClaimFromRole(selectedRole, claim)}
                            disabled={isLoading}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleManagementModal;
